import React, { useMemo } from 'react';
import { Box, Stepper } from '@lucid/lego';
import type { Step } from '@lucid/lego';
import styles from './OrderStatusStepper.module.scss';

type OrderStatusStepperProps = {
  orderStatus?: string;
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  dataTestId?: string;
};

/** Map of normalised (lower-case) order status → step index. */
const STATUS_STEP_MAP: Record<string, number> = {
  // --- Mapped display statuses (from BE transformation) ---
  'awaiting assignment': 0,
  'in production': 1,
  'factory gated': 2,
  'production complete': 2, // Legacy → Factory Gated
  'in transit to eu fpr': 3,
  'in transit': 3, // Legacy → In Transit to EU FPR
  'vehicle at eu fpr': 4,
  'vehicle at port': 4, // Legacy → Vehicle at EU FPR
  'vehicle ready for dispatch': 5,
  'vehicle dispatched': 6,
  'vehicle on-site': 7,
  delivered: 8,
  // --- Raw SFDC statuses (when BE mapping is not yet applied) ---
  'in transit to port': 3,
  'at port': 4,
  'in transit to pdi center': 4,
  'at pdi center': 4,
  'pdi completed': 4,
  'ready for transport': 5,
  'in transit to delivery center': 6,
  'at delivery center': 7,
};

/**
 * Builds the ordered step definitions with localised labels.
 */
const buildStepDefinitions = (labels: Record<string, string>): Step[] => [
  {
    id: 'awaiting-assignment',
    label: labels?.awaitingAssignment || 'Awaiting assignment',
    status: 'pending',
  },
  { id: 'in-production', label: labels?.inProduction || 'In Production', status: 'pending' },
  { id: 'factory-gated', label: labels?.factoryGated || 'Factory Gated', status: 'pending' },
  {
    id: 'in-transit-to-eu-fpr',
    label: labels?.inTransitToEuFpr || 'In Transit to EU FPR',
    status: 'pending',
  },
  {
    id: 'vehicle-at-eu-fpr',
    label: labels?.vehicleAtEuFpr || 'Vehicle at EU FPR',
    status: 'pending',
  },
  {
    id: 'vehicle-ready-for-dispatch',
    label: labels?.vehicleReadyForDispatch || 'Vehicle ready for dispatch',
    status: 'pending',
  },
  {
    id: 'vehicle-dispatched',
    label: labels?.vehicleDispatched || 'Vehicle dispatched',
    status: 'pending',
  },
  { id: 'vehicle-on-site', label: labels?.vehicleOnSite || 'Vehicle On-site', status: 'pending' },
  { id: 'delivered', label: labels?.delivered || 'Delivered', status: 'pending' },
];

/**
 * Order status stepper — renders a horizontal progress bar reflecting the
 * current fulfilment stage of an order.
 *
 * @component
 * @param {OrderStatusStepperProps} props
 * @returns {JSX.Element}
 * @example
 * <OrderStatusStepper orderStatus="In Production" theme="dark" labels={labels} />
 */
const OrderStatusStepper: React.FC<OrderStatusStepperProps> = ({
  orderStatus,
  theme,
  labels,
  dataTestId = 'order-status-stepper',
}) => {
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';

  const steps = useMemo<Step[]>(() => {
    const normalised = orderStatus?.toLowerCase() || '';
    const definitions = buildStepDefinitions(labels);
    const currentIndex = STATUS_STEP_MAP[normalised] ?? -1;

    definitions.forEach((step, index) => {
      if (index < currentIndex) {
        step.status = 'completed';
      } else if (index === currentIndex) {
        step.status = normalised === 'delivered' ? 'completed' : 'active';
      }
    });

    return definitions;
  }, [orderStatus, labels]);

  return (
    <Box
      mt={16}
      pt={32}
      pl={24}
      pb={32}
      pr={24}
      mb={24}
      background={theme === 'dark' ? 'white' : 'black'}
      className={styles.stepperContainer}
    >
      <Stepper steps={steps} theme={invertedTheme} dataTestId={dataTestId} />
    </Box>
  );
};

OrderStatusStepper.displayName = 'OrderStatusStepper';

export default OrderStatusStepper;
